import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import HeadlineTiles from "@/components/home/HeadlineTiles"
import ChannelBars from "@/components/home/ChannelBars"
import MarketsTable from "@/components/home/MarketsTable"
import MonthlyRevenueChart from "@/components/home/MonthlyRevenueChart"
import { type CompanyRevenue, type MarketSnapshot, monthLabel } from "@/components/home/types"
import { inventory_overview_path, org_revenue_path } from "@/lib/routes"
import { formatDate } from "./Revenue/format"
import { rateLines } from "./Revenue/rates"

interface Props {
  company: CompanyRevenue
  markets: MarketSnapshot[]
}

// Markets after the first reuse the first series color at a lighter mix, the
// same way the all-markets Revenue chart tells markets apart.
const MARKET_TINT = [100, 55, 35]
const marketColor = (index: number) => {
  const pct = MARKET_TINT[Math.min(index, MARKET_TINT.length - 1)]
  return pct === 100 ? "var(--series-1)" : `color-mix(in srgb, var(--series-1) ${pct}%, var(--card))`
}

export default function Dashboard({ company, markets }: Props) {
  const cur = company.display_currency
  const charted = markets.filter((m) => !company.unconverted_markets.includes(m.name))
  const rateNote = [...rateLines(cur, markets.map((m) => m.currency), company.rates), company.rate_date ? formatDate(company.rate_date) : null]
    .filter(Boolean)
    .join(" · ")

  if (markets.length === 0) {
    return (
      <AppLayout header={<PageTitle title="Dashboard" subtitle="How the company is doing, all markets" />}>
        <p className="text-sm">No markets yet. Add a country-level org node to get started.</p>
      </AppLayout>
    )
  }

  return (
    <AppLayout header={<PageTitle title="Dashboard" subtitle="How the company is doing, all markets" />}>
      <div className="flex flex-col gap-4">
        <HeadlineTiles
          currency={cur}
          currencyInLabel
          revenueThisYear={company.revenue_this_year}
          revenueThisMonth={company.revenue_this_month}
          changePct={company.change_pct}
          plan={{
            target: company.plan_progress.target,
            revenue: company.plan_progress.revenue,
            notCounted: company.plan_progress.not_counted,
            emptyText: company.plan_progress.not_counted.length < markets.length && company.unconverted_markets.length > 0
              ? "Blank until every market has an exchange rate"
              : undefined,
          }}
          needBuying={company.need_buying}
          needBuyingHref={inventory_overview_path()}
          excess={company.excess}
        />
        <div className="-mt-2 text-xs text-muted-foreground">
          Company revenue is converted to {cur} for display only. {rateNote}
          {company.unconverted_markets.length > 0 &&
            ` ${company.unconverted_markets.join(", ")} has no exchange rate yet, so company totals are blank. Rates refresh nightly.`}
        </div>

        <MarketsTable markets={markets} />

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <MonthlyRevenueChart
              title="Revenue by month"
              subtitle={`All markets, in ${cur}`}
              currency={cur}
              data={company.monthly.map((m) => ({ label: monthLabel(m.key), ...m.markets }))}
              bars={charted.map((m, i) => ({ key: m.id, name: m.name, color: marketColor(i) }))}
              footer={{ label: "Open Revenue", href: org_revenue_path() }}
            />
          </div>
          <ChannelBars channels={company.channels} currency={cur} subtitle={`This month so far, all markets, in ${cur}`} />
        </div>
      </div>
    </AppLayout>
  )
}
